import { useEffect, useState } from "react";
import { Infinity as InfinityIcon, AlertTriangle } from "lucide-react";
import { http } from "../lib/api";

/**
 * QuotaBar — barre d'utilisation compacte affichée sous le titre principal
 * des onglets Factures / Produits / Fiches recettes.
 *
 * Récupère les compteurs live via GET /api/plan/me (limits + usage) et écoute
 * les CustomEvents `rentascan:usage-changed` + `rentascan:invoice-validated`
 * pour se rafraîchir immédiatement après validation / création / suppression.
 *
 * Props:
 *   - resource: "invoices" | "products" | "recipes"
 *   - label:    Nom affiché à gauche de la barre
 *
 * Couleurs:
 *   0-69%   bleu profond (#0B1F33)
 *   70-89%  jaune orangé (#F5C542)
 *   90-99%  orange soutenu (#FF9F1C)
 *   100%    rouge + "Limite atteinte" (#D84343)
 *   Intégral (unlimited): bleu #1769E0 avec ∞ + "Illimité"
 */
export default function QuotaBar({ resource, label }) {
  const [state, setState] = useState(null);

  const load = () => http.get("/plan/me").then((r) => setState(r.data)).catch(() => setState(null));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener("rentascan:usage-changed", onChange);
    window.addEventListener("rentascan:invoice-validated", onChange);
    return () => {
      window.removeEventListener("rentascan:usage-changed", onChange);
      window.removeEventListener("rentascan:invoice-validated", onChange);
    };
  }, []);

  if (!state?.limits || !state?.usage) return null;

  const map = {
    invoices: { used: state.usage.invoices_this_month, limit: state.limits.invoices_per_month, suffix: "analysée(s) ce mois-ci" },
    products: { used: state.usage.products,            limit: state.limits.products_max,       suffix: "au catalogue" },
    recipes:  { used: state.usage.recipes,             limit: state.limits.recipes_max,        suffix: "créée(s)" },
  }[resource];
  if (!map) return null;

  const UNLIMITED = 999_999_999;
  const isUnlimited = map.limit >= UNLIMITED;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((map.used / Math.max(1, map.limit)) * 100));
  const remaining = Math.max(0, map.limit - map.used);

  const bar = (() => {
    if (isUnlimited) return { bg: "#1769E0", tag: "bg-[#DCEBFF] text-[#1769E0]" };
    if (pct >= 100)  return { bg: "#D84343", tag: "bg-[#FDECEC] text-[#7C2A21]" };
    if (pct >= 90)   return { bg: "#FF9F1C", tag: "bg-[#FFF0D2] text-[#7A4C0B]" };
    if (pct >= 70)   return { bg: "#F5C542", tag: "bg-[#FFF7E5] text-[#7A4C0B]" };
    return { bg: "#0B1F33", tag: "bg-[#F4F5EF] text-[#0B1F33]" };
  })();

  const nextReset = state.usage.invoices_reset_at
    ? new Date(state.usage.invoices_reset_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div
      className="surface p-4 flex flex-col gap-2"
      data-testid={`quota-bar-${resource}`}
      role="status"
      aria-label={`${label} — ${map.used}${isUnlimited ? "" : ` / ${map.limit}`}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap min-w-0">
          <span className="text-[13px] font-heading font-extrabold text-[#0B1F33]">{label}</span>
          <span className="text-[13px] font-mono-num tabular-nums text-[#5E6875]">
            <b className="text-[#0B1F33]">{map.used}</b>
            {isUnlimited ? (
              <> {map.suffix} · <span className="inline-flex items-center gap-1 text-[#1769E0] font-bold"><InfinityIcon className="w-3.5 h-3.5" /> Illimité</span></>
            ) : (
              <> sur <b className="text-[#0B1F33]">{map.limit}</b> {map.suffix}</>
            )}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest ${bar.tag}`}>
          {isUnlimited ? "Intégral" : pct >= 100 ? (<><AlertTriangle className="w-3 h-3" /> Limite atteinte</>) : `${pct} %`}
        </span>
      </div>

      <div className="relative h-2 rounded-full bg-[#F4F5EF] overflow-hidden" aria-hidden>
        <div
          className="absolute inset-y-0 left-0 transition-all duration-500"
          style={{
            width: isUnlimited ? "100%" : `${pct}%`,
            background: bar.bg,
            backgroundImage: isUnlimited
              ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 8px, transparent 8px 16px)"
              : undefined,
          }}
        />
      </div>

      {/* Secondary line — reset date for invoices, remaining count when close to limit */}
      <div className="text-[11.5px] text-[#5E6875] flex flex-wrap gap-x-3">
        {resource === "invoices" && nextReset && (
          <span>Prochain reset du quota mensuel · <b className="text-[#0B1F33]">{nextReset}</b></span>
        )}
        {!isUnlimited && pct >= 80 && pct < 100 && (
          <span className="text-[#7A4C0B] font-semibold">
            Vous approchez de votre limite · il reste <b>{remaining}</b> {resource === "invoices" ? "facture(s)" : resource === "products" ? "produit(s)" : "fiche(s)"}.
          </span>
        )}
        {pct >= 100 && !isUnlimited && (
          <span className="text-[#7C2A21] font-semibold">Passez à une formule supérieure pour créer davantage — vos données existantes restent intactes.</span>
        )}
      </div>
    </div>
  );
}
